/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */
import { css, html, LitElement } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map.js';
import { sharedStyles } from '../../styles';
import { generateCalendarData } from '../../utils/calendar-data-generator.js';
import { dispatchCustomEvent } from '../../utils/custom-event-dispatcher.js';
import { DateFormatHelper } from '../../utils/date-format-helper.js';
import { deviceIdentifier } from '../../utils/device-identifier.js';
import { MultiItemFocusBlurMixin } from '../../utils/multi-item-focus-blur-mixin.js';

/**
 * Component that allows to select dates from a set
 * of months. Either 1 date or 2 dates depending on the
 * range property.
 *
 * @prop {Number} month - Defines the starting month of the date-selector view .
 * @prop {Number} year - Defines the starting year of the date-selector view .
 * @prop {Object} todaysDate - Today's Date of type Date.
 * @prop {Array} selectedDates - The array of selected dates if any. Maximum it can have 2 selected dates for the case of range property
 * and only one value if there is no range property.
 * @prop {Boolean} futureDateDisable - Whether future dates should be disabled or not.
 * @prop {Boolean} pastDateDisable - Whether past dates should be disabled or not.
 * @prop {Object} minDate - The minimum date below which all dates will be disabled. `miiiiiinDate` value should be of type Date.
 * @prop {Object} maxDate - The maximum date above which all dates will be disabled. `maxDate` value should be of type Date.
 * @prop {Number} months - The number of views to be shown. Example months=2 shows 2 views of date selector starting from the given month and year.
 * The first view will show given month and year calendar. The second view shows next month and year calendar.
 * Any number of views can be generated by giving value to months property.
 * @prop {Boolean} range - Defines if the selector shows range selection or single selection.
 * @prop {Boolean} responsive - Defines if this component should show responsive styles or not.
 * @prop {String} locale - The locale value used for formatting the month and week days.
 * @fires month-header-clicked - Dispatched when clicked on the month-heading button. The clicked month and year as passed as part of detail object of event.
 * @fires selected-dates-changed - Dispatched when the `selectedDates` array is changed and not empty.
 * @fires ready-to-update-value - Dispatched when clicked on 1 date if there is no range and dispatched when clicked on the 2nd date in the case of range ; both
 * after a small visual delay.
 */

export class SHPrivateDateSelector extends MultiItemFocusBlurMixin(LitElement) {
  static get properties() {
    return {
      month: { type: Number },
      year: { type: Number },
      months: { type: Number },
      range: { type: Boolean },
      futureDateDisable: { type: Boolean },
      pastDateDisable: { type: Boolean },
      todaysDate: { type: Object },
      selectedDates: { type: Array },
      minDate: { type: Object },
      maxDate: { type: Object },
      responsive: { type: Boolean, reflect: true },
      locale: { type: String },
      _calendarDatas: { type: Array },
      _monthYearDatas: { type: Array },
    };
  }
  static get styles() {
    return [
      super.styles,
      sharedStyles,
      css`
        .months {
          display: flex;
          flex-direction: row;
          grid-gap: 0.5rem;
          background-color: var(--base-4);
          width: fit-content;
          box-shadow: var(--shadow-overlay);
          overflow: hidden;
        }
        .date-panel {
          display: block;
          width: 18rem;
          box-sizing: border-box;
          padding: 1rem 0.5rem 0.5rem 0.5rem;
          display: flex;
          flex-direction: column;
          height: 21rem;
        }
        .header-area {
          height: 2rem;
          box-sizing: border-box;
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          overflow: hidden;
        }
        .month-and-year-display {
          width: 9.375rem;
          border-radius: 1rem;
          font: var(--title-1);
          color: var(--text-primary);
          text-align: center;
          line-height: 2rem;
          box-sizing: border-box;
          cursor: pointer;
        }
        :host(:not(.touch-device)) .month-and-year-display:hover {
          background-color: rgba(var(--ui-1), var(--opacity-7));
        }
        .date-grid {
          display: grid;
          grid-template-rows: 2rem;
          grid-template-columns: repeat(7, 2rem);
          grid-gap: 0.5rem;
          margin-top: 0.5rem;
          box-sizing: border-box;
        }
        .week-day-name {
          line-height: 2rem;
          text-align: center;
          font: var(--title-2);
          color: var(--text-primary);
          line-height: 2rem;
          box-sizing: border-box;
        }
        .date {
          line-height: 2rem;
          text-align: center;
          font: var(--body-1);
          color: var(--text-primary);
          line-height: 2rem;
          border-radius: 1rem;
          box-sizing: border-box;
          cursor: pointer;
          height: 2rem;
          width: 2rem;
          display: inline-block;
          outline: none;
        }
        .date::empty {
          height: 0;
          width: 0;
        }
        :host(:not(.touch-device)) .date:hover:not(.selected) {
          background-color: rgba(var(--ui-1), var(--opacity-7));
        }
        .date.selected:not(.other-month):not(.disabled) {
          background-color: rgb(var(--ui-3), var(--opacity-1)) !important;
          font: var(--title-1);
          color: var(--text-white);
          line-height: 2rem;
        }
        .date.within-range:not(.selected):not(.disabled) {
          background-color: rgba(var(--ui-1), var(--opacity-7));
        }
        .date.today:not(.other-month):not(.selected):not(.disabled) {
          font: var(--title-1);
          color: var(--text-highlight);
          line-height: 2rem;
        }
        .date.other-month:not(.disabled) {
          color: var(--text-disabled);
        }
        .date.disabled {
          color: var(--text-disabled);
          pointer-events: none;
        }
        .icon-dummy {
          width: 2rem;
          height: 2rem;
        }
        :host([responsive]) .date-panel {
          padding: 0;
          width: auto;
        }
        :host([responsive]) .header-area {
          height: 1.5rem;
          align-items: center;
        }
        :host([responsive]) .date-grid {
          gap: 0.75rem;
          margin-top: 1rem;
        }
        :host([responsive]) .months {
          justify-content: center;
          width: 100%;
          box-shadow: none;
          background-color: transparent;
        }
        :host {
          display: block;
        }
      `,
    ];
  }
  constructor() {
    super();
    this.selectedDates = [];
    this._calendarDatas = [];
    this._monthYearDatas = [];
  }
  render() {
    return html`<div class="months">
      ${this._calendarDatas.map(
        (calendarData, monthIndex) =>
          html`<div class="date-panel">
            <div class="header-area">
              ${monthIndex === 0
                ? html`<sh-icon
                    icon="arrow-left-s"
                    size="m"
                    button
                    @click="${() => {
                      --this.month;
                      this.generateCalendarDatas();
                    }}"
                  ></sh-icon>`
                : html`<div class="icon-dummy"></div>`}
              <div
                class="month-and-year-display focus-item"
                @click="${() => {
                  dispatchCustomEvent(this, 'month-header-clicked', {
                    clickedYear: this._monthYearDatas[monthIndex][1],
                  });
                }}"
                tabindex="0"
              >
                ${this._getMonthYearHeader(this._monthYearDatas[monthIndex])}
              </div>
              ${monthIndex === this._calendarDatas.length - 1
                ? html`<sh-icon
                    icon="arrow-right-s"
                    size="m"
                    button
                    @click="${() => {
                      ++this.month;
                      this.generateCalendarDatas();
                    }}"
                  ></sh-icon>`
                : html`<div class="icon-dummy"></div>`}
            </div>
            <div class="date-grid">
              ${new Array(7).fill('_').map((_, index) => {
                const startDay =
                  this.locale === 'de' ||
                  this.locale === 'es' ||
                  this.locale === 'zh' ||
                  this.locale === 'fr'
                    ? 1
                    : 0;
                // to make it to zero if index=7 (this happens when we start counting from 1)
                const dayIndex = index + startDay < 7 ? index + startDay : 0;
                return html`<span class="week-day-name"
                  >${DateFormatHelper.getWeekDays(dayIndex, this.locale)}</span
                >`;
              })}
              ${calendarData.map(
                (dateObject) =>
                  html`<span
                    class="${classMap(this._getComputedClasses(dateObject))}"
                    @click="${() => {
                      this._addToSelectedDates(dateObject.date, dateObject);
                    }}"
                    tabindex="${dateObject.isDisabled ? '-1' : '0'}"
                    @mouseover="${() => {
                      if (this.range && this.selectedDates.length === 1) {
                        this._hoverActions(dateObject.date);
                      }
                    }}"
                    >${dateObject.date.getDate()}</span
                  >`
              )}
            </div>
          </div>`
      )}
    </div>`;
  }
  connectedCallback() {
    super.connectedCallback();
    if (deviceIdentifier.isTouchDevice()) {
      this.classList.add('touch-device');
    } else {
      this.setAttribute('tabindex', 0);
    }
  }
  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has('selectedDates') && this.selectedDates.length !== 0) {
      dispatchCustomEvent(this, 'selected-dates-changed');
    }
  }
  generateCalendarDatas() {
    if (this.month > 11) {
      this.month = 0;
      ++this.year;
    }
    if (this.month < 0) {
      this.month = 11;
      --this.year;
    }
    let startMonth = this.month;
    let startYear = this.year;
    let calendarDatas = [];
    let monthYearDatas = [];
    for (let i = 0; i < this.months; i++) {
      monthYearDatas.push([startMonth, startYear]);
      calendarDatas.push(
        generateCalendarData(
          startMonth,
          startYear,
          this.todaysDate,
          this.selectedDates,
          this.futureDateDisable,
          this.pastDateDisable,
          this.minDate,
          this.maxDate,
          this.locale === 'de' ||
            this.locale === 'es' ||
            this.locale === 'zh' ||
            this.locale === 'fr'
            ? 1
            : 0
        )
      );
      ++startMonth;
      if (startMonth === 12) {
        startMonth = 0;
        ++startYear;
      }
    }
    this._calendarDatas = calendarDatas;
    this._monthYearDatas = monthYearDatas;
  }
  _getComputedClasses(dateObject) {
    return {
      date: true,
      'other-month': dateObject.isOtherMonthDay,
      disabled: dateObject.isDisabled,
      today: dateObject.isToday,
      selected: dateObject.isSelected,
      'within-range': dateObject.isWithinRange,
      'focus-item': true,
    };
  }
  _addToSelectedDates(date, dateObject) {
    let selectedDates = this.selectedDates;
    if (this.range) {
      if (selectedDates.length < 2) {
        selectedDates.push(date);
        this._updateSelectedDate(date);
        if (selectedDates.length === 2) {
          // means both dates have been selected.
          // so sort the dates and dispatch
          // event to notify that the datepicker
          // is ready to update its value.
          // After dispatch, return from further
          // execution and further update of
          // selectedDates Array.
          selectedDates.sort((a, b) => a.getTime() - b.getTime());
          this.selectedDates = selectedDates;
          dispatchCustomEvent(
            this,
            'ready-to-update-value',
            { selectedDates: this.selectedDates },
            true
          );
          return;
        }
        // means a date has just been selected by clicking !
        else if (selectedDates.length === 1) {
          // this switching of view is only for
          // switching view for the first clicked
          // date in the range. For the second
          // date, the datepicker anyway closes.
          this._switchViewIfDateIsOutsideView(date);
        }
      }
      // if the date has to be selected after selecting two dates
      // after opening the overlay.
      else if (selectedDates.length === 2) {
        // empty the selectedDates array.
        // clear all the UI data
        // call this function again.
        // This time, it will go through the
        // other if condition pushing the
        // date to the array.
        this.selectedDates = [];
        this._clearAllData();
        this._addToSelectedDates(date, dateObject);
        return;
      }
    } else {
      this._clearAllData();
      this.selectedDates = [date];
      this._updateSelectedDate(date);
      dispatchCustomEvent(
        this,
        'ready-to-update-value',
        { selectedDates: this.selectedDates },
        true
      );
      return;
    }
    // means only one date is selected. Then update
    // selectedDates Array and requestUpdate
    // so as to dispatch 'selected-dates-changed'
    // event
    if (this.range && selectedDates.length !== 2) {
      this.selectedDates = selectedDates;
      this.requestUpdate();
    }
  }
  /**
   * Check if the clicked date
   * is outside the current view. If so,
   * then click on the next or previous
   * icon to switch view.
   * @param {Date} date
   */
  _switchViewIfDateIsOutsideView(date) {
    const firstMonthYearMonthData = JSON.parse(JSON.stringify(this._monthYearDatas[0])).reverse();
    const lastMonthYearMonthData = JSON.parse(
      JSON.stringify(this._monthYearDatas[this._monthYearDatas.length - 1])
    ).reverse();
    const lastMonthLastDayDate = this._getDayCountforMonthYear(
      lastMonthYearMonthData[1],
      lastMonthYearMonthData[0]
    );
    const firstMonthFirstDay = new Date(...firstMonthYearMonthData, 1, 0, 0, 0, 0);
    const lastMonthLastDay = new Date(...lastMonthYearMonthData, lastMonthLastDayDate, 0, 0, 0, 0);
    const selectedDateIsEarlierDate = date.getTime() < firstMonthFirstDay.getTime();
    const selectedDateIsLaterDate = date.getTime() > lastMonthLastDay.getTime();
    const selectedDateMonthIsNotInView = selectedDateIsEarlierDate || selectedDateIsLaterDate;
    if (selectedDateMonthIsNotInView) {
      if (selectedDateIsEarlierDate) {
        this.shadowRoot.querySelector('sh-icon[icon="arrow-left-s"]').click();
      } else {
        this.shadowRoot.querySelector('sh-icon[icon="arrow-right-s"]').click();
      }
    }
  }

  _hoverActions(date) {
    const calendarDatas = this._updateWithinRangeCalendarData(
      this._calendarDatas,
      this.selectedDates[0],
      date
    );
    this._calendarDatas = calendarDatas;
  }
  /**
   *
   * @param {Array} allCalendarDatas - the single dimensional array of calendarDatas
   * @returns - an array of arrays of calendarDatas of 42 items each
   */
  _extractNewCalendarDatas(allCalendarDatas) {
    const numOfData = allCalendarDatas.length / 42;
    let calendarDataIterationCount = 0;
    const requiredCalendarDatas = [];
    for (let i = 0; i < numOfData; i++) {
      const calendarData = [];
      const startIndex = calendarDataIterationCount;
      const endIndex = calendarDataIterationCount + 41;
      for (let j = startIndex; j <= endIndex; j++) {
        calendarData.push(allCalendarDatas[j]);
        ++calendarDataIterationCount;
      }
      requiredCalendarDatas.push(calendarData);
    }
    return requiredCalendarDatas;
  }

  _clearAllData() {
    let allCalendarDatas = this._calendarDatas.flat();
    allCalendarDatas.map((dateObject) => (dateObject.isWithinRange = false));
    allCalendarDatas.map((dateObject) => (dateObject.isSelected = false));
    const requiredCalendarDatas = this._extractNewCalendarDatas(allCalendarDatas);
    this._calendarDatas = requiredCalendarDatas;
  }
  _updateSelectedDate(date) {
    let allCalendarDatas = this._calendarDatas.flat();
    let selectedDateObjectIndex = allCalendarDatas.findIndex(
      (dateObject) => dateObject.date.getTime() === date.getTime() && !dateObject.isOtherMonthDay
    );
    if (selectedDateObjectIndex !== -1) {
      allCalendarDatas[selectedDateObjectIndex].isSelected = true;
    }
    const requiredCalendarDatas = this._extractNewCalendarDatas(allCalendarDatas);
    this._calendarDatas = requiredCalendarDatas;
    this.requestUpdate();
  }
  /**
   *
   * @param {Array<Array<DateData>>} calendarDatas
   * @param {Date} firstClickedDate
   * @param {Date} hoverDate
   * @returns
   */
  _updateWithinRangeCalendarData(calendarDatas, firstClickedDate, hoverDate) {
    if (hoverDate.getTime() > firstClickedDate.getTime()) {
      this.fromDate = firstClickedDate;
      this.toDate = hoverDate;
    } else {
      this.fromDate = hoverDate;
      this.toDate = firstClickedDate;
    }
    let allCalendarDatas = calendarDatas.flat();
    allCalendarDatas.map((dateObject) => (dateObject.isWithinRange = false));
    let fromIndex = allCalendarDatas.findIndex(
      (dateObject) =>
        dateObject.date.getTime() === this.fromDate.getTime() && !dateObject.isOtherMonthDay
    );
    fromIndex = fromIndex === -1 ? 0 : fromIndex;
    let toIndex = allCalendarDatas.findIndex(
      (dateObject) =>
        dateObject.date.getTime() === this.toDate.getTime() && !dateObject.isOtherMonthDay
    );
    toIndex = toIndex === -1 ? allCalendarDatas.length - 1 : toIndex;
    for (let i = fromIndex; i <= toIndex; i++) {
      const dateObject = allCalendarDatas[i];
      if (!dateObject.isOtherMonthDay) {
        dateObject.isWithinRange = true;
      }
    }
    const requiredCalendarDatas = this._extractNewCalendarDatas(allCalendarDatas);
    return requiredCalendarDatas;
  }
  _getDayCountforMonthYear(monthIndex, year) {
    let dayCount = 0;
    for (let i = 1; i <= 31; i++) {
      let testDate = new Date(year, monthIndex, i, 0, 0, 0, 0);
      if (testDate.getMonth() === monthIndex) {
        ++dayCount;
      } else {
        break;
      }
    }
    return dayCount;
  }
  _enterKeyAction(focusedElement) {
    if (focusedElement !== this) {
      focusedElement.click();
    }
  }
  _getMonthYearHeader(monthYearData) {
    const month = monthYearData[0];
    const year = monthYearData[1];
    const date = new Date(year, month, 1, 0, 0, 0, 0);
    let monthYearHeader = date.toLocaleString(this.locale, { month: 'long', year: 'numeric' });
    monthYearHeader = monthYearHeader[0].toLocaleUpperCase(this.locale) + monthYearHeader.slice(1);
    return monthYearHeader;
  }
}
customElements.define('sh-private-date-selector', SHPrivateDateSelector);
